import { useCallback, useEffect, useId, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from '@/hooks/useTranslation';
import {
  persistConsent,
  persistConsentPrefs,
  readStoredConsent,
  readStoredConsentPrefs,
  type LyrraConsentChoice,
  type LyrraConsentPrefs,
} from '@/lib/google-consent';
import { Switch } from '@/components/ui/Switch';
import { cn } from '@/lib/utils';

/**
 * Bandeau cookies + modal de paramétrage (préférences réelles avant acceptation).
 * La modal s’ouvre au-dessus du voile, pour pouvoir lire / choisir sans naviguer en arrière-plan.
 */
export function CookieConsentBanner() {
  const { t } = useTranslation();
  const [open, setOpen] = useState(() => readStoredConsent() === null);
  const [prefsOpen, setPrefsOpen] = useState(false);
  const [prefs, setPrefs] = useState<LyrraConsentPrefs>(() => (
    readStoredConsentPrefs() ?? { analytics: false, marketing: false }
  ));
  const titleId = useId();
  const prefsTitleId = useId();

  const choose = useCallback((choice: LyrraConsentChoice) => {
    persistConsent(choice);
    setOpen(false);
    setPrefsOpen(false);
  }, []);

  const savePrefs = useCallback(() => {
    persistConsentPrefs(prefs);
    setOpen(false);
    setPrefsOpen(false);
  }, [prefs]);

  useEffect(() => {
    if (!open && !prefsOpen) return;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prevOverflow;
    };
  }, [open, prefsOpen]);

  if (!open && !prefsOpen) return null;

  const title = String(t('consent.bannerTitle'));
  const description = String(t('consent.bannerDescription'));
  const acceptAll = String(t('consent.acceptAll'));
  const essentialOnly = String(t('consent.essentialOnly'));
  const manage = String(t('consent.manageCookies'));
  const region = String(t('consent.regionNotice'));
  const prefsTitle = String(t('consent.prefsTitle'));
  const prefsIntro = String(t('consent.prefsIntro'));
  const essentialLabel = String(t('consent.catEssential'));
  const essentialDesc = String(t('consent.catEssentialDesc'));
  const analyticsLabel = String(t('consent.catAnalytics'));
  const analyticsDesc = String(t('consent.catAnalyticsDesc'));
  const marketingLabel = String(t('consent.catMarketing'));
  const marketingDesc = String(t('consent.catMarketingDesc'));
  const saveLabel = String(t('consent.savePrefs'));
  const backLabel = String(t('consent.backToBanner'));
  const alwaysOn = String(t('consent.alwaysOn'));
  const policyHint = String(t('consent.policyHint'));

  return (
    <div className="fixed inset-0 z-[100]" role="presentation">
      <div
        className="absolute inset-0 bg-slate-900/50 backdrop-blur-[2px]"
        aria-hidden="true"
      />

      {prefsOpen ? (
        <div
          role="dialog"
          aria-modal="true"
          aria-labelledby={prefsTitleId}
          className="absolute inset-0 z-[110] flex items-end justify-center p-0 sm:items-center sm:p-4"
        >
          <div className="flex max-h-[min(92vh,40rem)] w-full max-w-lg flex-col overflow-hidden rounded-t-2xl border border-slate-200 bg-white shadow-[0_-12px_40px_rgba(15,23,42,0.22)] sm:rounded-2xl">
            <header className="shrink-0 border-b border-slate-100 px-5 py-4">
              <p id={prefsTitleId} className="text-base font-bold text-slate-900">
                {prefsTitle}
              </p>
              <p className="mt-1 text-sm leading-relaxed text-slate-600">{prefsIntro}</p>
            </header>

            <div className="min-h-0 flex-1 space-y-3 overflow-y-auto px-5 py-4">
              <ConsentCategoryRow
                title={essentialLabel}
                description={essentialDesc}
                checked
                disabled
                badge={alwaysOn}
              />
              <ConsentCategoryRow
                title={analyticsLabel}
                description={analyticsDesc}
                checked={prefs.analytics}
                onChange={(analytics) => setPrefs((p) => ({ ...p, analytics }))}
              />
              <ConsentCategoryRow
                title={marketingLabel}
                description={marketingDesc}
                checked={prefs.marketing}
                onChange={(marketing) => setPrefs((p) => ({ ...p, marketing }))}
              />
              <p className="pt-1 text-xs leading-relaxed text-slate-500">
                {policyHint}{' '}
                <Link
                  to="/cookies"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="font-semibold text-[color:var(--inst-player-primary-deep,#061128)] underline-offset-2 hover:underline"
                >
                  {String(t('consent.policyLink'))}
                </Link>
              </p>
            </div>

            <footer className="flex shrink-0 flex-col gap-2 border-t border-slate-100 px-5 py-4 sm:flex-row sm:justify-end">
              <button
                type="button"
                className="inline-flex items-center justify-center rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-sm font-semibold text-slate-800 transition hover:bg-slate-50"
                onClick={() => setPrefsOpen(false)}
              >
                {backLabel}
              </button>
              <button
                type="button"
                className="inline-flex items-center justify-center rounded-xl bg-[color:var(--inst-player-primary-deep,#061128)] px-4 py-2.5 text-sm font-semibold text-white transition hover:opacity-95"
                onClick={savePrefs}
                autoFocus
              >
                {saveLabel}
              </button>
            </footer>
          </div>
        </div>
      ) : open ? (
        <div className="absolute inset-0 z-[105] flex flex-col justify-end">
          <div
            role="dialog"
            aria-modal="true"
            aria-labelledby={titleId}
            className="relative border-t border-slate-200 bg-white p-4 pb-[max(1rem,env(safe-area-inset-bottom))] shadow-[0_-12px_40px_rgba(15,23,42,0.18)]"
          >
            <div className="mx-auto flex max-w-5xl flex-col gap-4 md:flex-row md:items-center md:justify-between md:gap-6">
              <div className="min-w-0 flex-1">
                <p id={titleId} className="text-sm font-bold text-slate-900">
                  {title}
                </p>
                <p className="mt-1 text-sm leading-relaxed text-slate-700">{description}</p>
                <p className="mt-2 text-xs text-slate-700">{region}</p>
              </div>
              <div className="flex flex-shrink-0 flex-col gap-2 sm:flex-row sm:flex-wrap sm:items-center sm:justify-end">
                <button
                  type="button"
                  className="inline-flex items-center justify-center rounded-xl bg-[color:var(--inst-player-primary-deep,#061128)] px-4 py-2.5 text-sm font-semibold text-white transition hover:opacity-95"
                  onClick={() => choose('all')}
                  autoFocus
                >
                  {acceptAll}
                </button>
                <button
                  type="button"
                  className="inline-flex items-center justify-center rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-sm font-semibold text-slate-800 transition hover:bg-slate-50"
                  onClick={() => choose('essential')}
                >
                  {essentialOnly}
                </button>
                <button
                  type="button"
                  className="inline-flex items-center justify-center rounded-xl px-3 py-2.5 text-sm font-semibold text-[color:var(--inst-player-primary-deep,#061128)] underline-offset-2 hover:underline"
                  onClick={() => {
                    setPrefs(readStoredConsentPrefs() ?? { analytics: false, marketing: false });
                    setPrefsOpen(true);
                  }}
                >
                  {manage}
                </button>
              </div>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}

function ConsentCategoryRow({
  title,
  description,
  checked,
  disabled,
  badge,
  onChange,
}: {
  title: string;
  description: string;
  checked: boolean;
  disabled?: boolean;
  badge?: string;
  onChange?: (value: boolean) => void;
}) {
  return (
    <div
      className={cn(
        'flex items-start gap-3 rounded-xl border border-slate-200 px-3.5 py-3',
        disabled && 'bg-slate-50',
      )}
    >
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-2">
          <p className="text-sm font-semibold text-slate-900">{title}</p>
          {badge ? (
            <span className="rounded-full bg-slate-200/80 px-2 py-0.5 text-[0.65rem] font-semibold uppercase tracking-wide text-slate-600">
              {badge}
            </span>
          ) : null}
        </div>
        <p className="mt-1 text-xs leading-relaxed text-slate-600">{description}</p>
      </div>
      <Switch
        checked={checked}
        disabled={disabled}
        onChange={(v) => onChange?.(v)}
        aria-label={title}
      />
    </div>
  );
}
